import type * as moment from 'moment';

import { type GridSelectionModel } from '@mui/x-data-grid-pro';

import type APIAddress from '~/types/APIAddress';
import type BusinessRule from '~/types/BusinessRule';
import type Order from '~/types/Order';
import type OrdersBatch from '~/types/OrdersBatch';
import type Parcel from '~/types/Parcel';

import { type ExtrasFormType } from '~/constants/form';
import { type PreCheckoutSteps } from '~/reducers/orders/orders.reducer';
import { type SavedAPIAddress } from '~/types/APIAddress';
import { type ReturnLabelBatchEntry } from '~/types/APIReturnLabels';
import { type AddressType, type BaseAddress } from '~/types/Address';
import {
  type OrderObjectId,
  type OrderSelection,
  type OrderTagsMapping,
  type OrderToBatchEntry,
} from '~/types/Order';
import {
  type BatchEntryExtended,
  type PartialFulfillmentContents,
  type ShipmentExtras,
} from '~/types/OrderBatchEntry';
import { type BatchEntryId } from '~/types/OrdersBatch';
import { type TrackingAppLocation, type TrackingReference } from '~/utils/TrackingHelper/types';

import { ActionKeys } from './constants';

export interface FetchOrderPayload {
  order: Order;
}

export const fetchOrder = (orderId: string) =>
  <const>{
    orderId,
    type: ActionKeys.ORDER_FETCH,
  };

export const fetchOrderSuccess = (payload: FetchOrderPayload) =>
  <const>{
    payload,
    type: ActionKeys.ORDER_FETCH_SUCCESS,
  };

export const orderClean = (orderId: string) =>
  <const>{
    orderId,
    type: ActionKeys.ORDER_CLEAN,
  };

export const fetchOrderError = () =>
  <const>{
    type: ActionKeys.ORDER_FETCH_ERROR,
  };

export const fetchOrders = (
  appLocation: TrackingAppLocation,
  page: number,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filters: any,
  fetchCount: string,
  showOnlyHidden: boolean,
  orderStatus: string,
  sort: string,
  sortBy: string,
  orderBy: string,
) =>
  <const>{
    payload: {
      appLocation,
      fetchCount,
      filters,
      orderBy,
      orderStatus,
      page,
      showOnlyHidden,
      sort,
      sortBy,
    },
    type: ActionKeys.ORDERS_FETCH,
  };

export const fetchOrdersSuccess = (orders: Order[], totalOrders: number, hidden: boolean) =>
  <const>{
    payload: {
      hidden,
      orders,
      totalOrders,
    },
    type: ActionKeys.ORDERS_FETCH_SUCCESS,
  };

export const fetchOrdersError = () =>
  <const>{
    type: ActionKeys.ORDERS_FETCH_ERROR,
  };

export const syncOrders = (appLocation: TrackingAppLocation) =>
  <const>{
    payload: {
      appLocation,
    },
    type: ActionKeys.ORDERS_SYNC,
  };

export const syncOrdersSuccess = () =>
  <const>{
    type: ActionKeys.ORDERS_SYNC_SUCCESS,
  };

export const syncOrdersError = () =>
  <const>{
    type: ActionKeys.ORDERS_SYNC_ERROR,
  };

export const pollOrdersStart = () =>
  <const>{
    type: ActionKeys.ORDERS_POLL_START,
  };

export const saveLastOrderId = (lastOrderId: string) =>
  <const>{
    lastOrderId,
    type: ActionKeys.ORDERS_SAVE_LAST_ORDER_ID,
  };

export const newOrdersFound = (newOrderId: string) =>
  <const>{
    newOrderId,
    type: ActionKeys.ORDERS_NEW_ORDERS_FOUND,
  };

export const getPackingSlips = (
  orderSelection: OrderSelection,
  windowRef: Window,
  appLocation: TrackingAppLocation,
) =>
  <const>{
    payload: {
      appLocation,
      orderSelection,
      windowRef,
    },
    type: ActionKeys.ORDERS_GET_PACKING_SLIP,
  };

export const getOrderPackingSlipError = () =>
  <const>{
    type: ActionKeys.ORDERS_GET_PACKING_SLIP_ERROR,
  };

export const createBatchSuccess = (batch: OrdersBatch) =>
  <const>{
    payload: {
      batch,
    },
    type: ActionKeys.BATCH_CREATE_SUCCESS,
  };

export const createBatchError = (err?: string) =>
  <const>{
    payload: {
      error: err,
    },
    type: ActionKeys.BATCH_CREATE_ERROR,
  };

export const hideOrders = (orderObjectIds: string[]) =>
  <const>{
    payload: {
      ids: orderObjectIds,
    },
    type: ActionKeys.ORDER_HIDE,
  };

export const hideOrderSuccess = () =>
  <const>{
    type: ActionKeys.ORDER_HIDE_SUCCESS,
  };

export const hideOrderError = (error: string) =>
  <const>{
    payload: {
      error,
    },
    type: ActionKeys.ORDER_HIDE_ERROR,
  };

export const orderEditBegin = (orderSelection?: OrderSelection) =>
  <const>{
    payload: {
      orderSelection,
    },
    type: ActionKeys.ORDER_BEGIN_EDIT_ACTION,
  };

export const orderEditEnd = () =>
  <const>{
    type: ActionKeys.ORDER_END_EDIT_ACTION,
  };

export const patchBatchEntries = (
  orderSelection: OrderSelection,
  parcel: Maybe<Parcel>,
  extras: Maybe<Partial<ShipmentExtras>>,
  trackingReference: TrackingReference,
  isAutoSave = false,
) =>
  <const>{
    payload: {
      extras,
      isAutoSave,
      orderSelection,
      parcel,
      trackingReference,
    },
    type: ActionKeys.ORDERS_PATCH_BATCH_ENTRIES,
  };

export const patchBatchEntriesSuccess = (batchEntries: BatchEntryExtended[]) =>
  <const>{
    payload: {
      batchEntries,
    },
    type: ActionKeys.ORDERS_PATCH_BATCH_ENTRIES_SUCCESS,
  };

/**
 * Optimistically flag the given batch entries as re-rating (PROCESSING_SHIPMENT) so the rate
 * column shows loading immediately, without an API call.
 */
export const markBatchEntriesProcessingShipment = (batchEntryIds: number[]) =>
  <const>{
    payload: { batchEntryIds },
    type: ActionKeys.ORDER_BATCH_ENTRIES_MARK_PROCESSING_SHIPMENT,
  };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const patchBatchEntriesError = (err: any) =>
  <const>{
    payload: {
      err,
    },
    type: ActionKeys.ORDERS_PATCH_BATCH_ENTRIES_ERROR,
  };

export const updateOrderParcel = (
  orderSelection: OrderSelection,
  parcel: Parcel,
  trackingReference: TrackingReference,
) =>
  <const>{
    payload: {
      orderSelection,
      parcel,
      trackingReference,
    },
    type: ActionKeys.ORDERS_UPDATE_PARCEL,
  };

export const updateOrderParcelSuccess = (batchEntries: BatchEntryExtended[]) =>
  <const>{
    payload: {
      batchEntries,
    },
    type: ActionKeys.ORDERS_UPDATE_PARCEL_SUCCESS,
  };

export const updateOrderParcelError = (err: unknown) =>
  <const>{
    payload: {
      err,
    },
    type: ActionKeys.ORDERS_UPDATE_PARCEL_ERROR,
  };

export const updateBatchEntriesExtras = (
  orderSelection: OrderSelection,
  extras: ShipmentExtras,
  form: ExtrasFormType,
) =>
  <const>{
    payload: {
      extras,
      form,
      orderSelection,
    },
    type: ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_EXTRAS,
  };

export const updateBatchEntriesExtrasSuccess = (batchEntries: BatchEntryExtended[]) =>
  <const>{
    payload: {
      batchEntries,
    },
    type: ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_EXTRAS_SUCCESS,
  };

export const patchUpdateBatchEntriesExtras = (
  orderSelection: OrderSelection,
  extras: Partial<ShipmentExtras>,
  callback?: () => void,
) =>
  <const>{
    payload: {
      callback, // This optional callback is to do anything after the update completes
      extras,
      orderSelection,
    },
    type: ActionKeys.ORDERS_PATCH_UPDATE_BATCH_ENTRIES_EXTRAS,
  };

export const patchUpdateBatchEntriesExtrasPackingSlipNote = (
  orderSelection: OrderSelection,
  packingSlipText: Maybe<string>,
  batchEntryIds?: number[],
) =>
  <const>{
    payload: {
      batchEntryIds,
      orderSelection,
      packingSlipText,
    },
    type: ActionKeys.ORDERS_PATCH_UPDATE_BATCH_ENTRIES_EXTRAS_PACKING_SLIP_NOTE,
  };

export const patchUpdateBatchEntriesExtrasSuccess = (batchEntries: BatchEntryExtended[]) =>
  <const>{
    payload: {
      batchEntries,
    },
    type: ActionKeys.ORDERS_PATCH_UPDATE_BATCH_ENTRIES_EXTRAS_SUCCESS,
  };

export const patchUpdateBatchEntriesExtrasError = (error: string) =>
  <const>{
    payload: {
      error,
    },
    type: ActionKeys.ORDERS_PATCH_UPDATE_BATCH_ENTRIES_EXTRAS_ERROR,
  };

export const patchUpdateBatchEntriesExtrasReset = () =>
  <const>{
    type: ActionKeys.ORDERS_PATCH_UPDATE_BATCH_ENTRIES_EXTRAS_RESET,
  };

export const updateBatchEntriesAddress = ({
  address,
  addressBookAddress,
  addressType,
  combineSenderReturnAddresses,
  createUserAddress,
  orderSelection,
  source,
  trackingReference,
  updateUserAddress,
}: {
  address: APIAddress | BaseAddress;
  addressBookAddress?: SavedAPIAddress;
  addressType: AddressType;
  combineSenderReturnAddresses: boolean;
  createUserAddress: boolean;
  orderSelection: OrderSelection;
  source: string;
  trackingReference: TrackingReference;
  updateUserAddress?: boolean;
}) =>
  <const>{
    payload: {
      address,
      addressBookAddress,
      addressType,
      combineSenderReturnAddresses,
      createUserAddress,
      orderSelection,
      source,
      trackingReference,
      updateUserAddress,
    },
    type: ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_ADDRESS,
  };

export const updateBatchEntriesAddressSuccess = (
  batchEntryCount: number,
  messageKey: string,
  batchEntries: BatchEntryExtended[],
  source: string,
) =>
  <const>{
    payload: {
      batchEntries,
      batchEntryCount,
      messageKey,
      source,
    },
    type: ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_ADDRESS_SUCCESS,
  };

export const updateBatchEntriesAddressError = (
  batchEntryCount: number,
  messageKey: string,
  source: string,
) =>
  <const>{
    payload: {
      batchEntryCount,
      messageKey,
      source,
    },
    type: ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_ADDRESS_ERROR,
  };

export const updateBatchEntriesServiceLevelToken = (
  orderSelection: OrderSelection,
  preferredServiceLevelToken: string,
  accountObjectId?: string,
) =>
  <const>{
    payload: {
      accountObjectId,
      orderSelection,
      preferredServiceLevelToken,
    },
    type: ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_SERVICE_LEVEL_TOKEN,
  };

export const updateBatchEntriesServiceLevelTokenSuccess = (batchEntries: BatchEntryExtended[]) =>
  <const>{
    payload: {
      batchEntries,
    },
    type: ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_SERVICE_LEVEL_TOKEN_SUCCESS,
  };

export const updateBatchEntriesServiceLevelTokenError = () =>
  <const>{
    type: ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_SERVICE_LEVEL_TOKEN_ERROR,
  };

export const countOrdersFetch = () =>
  <const>{
    type: ActionKeys.ORDERS_COUNT_FETCH,
  };

export const countOrderSuccess = (count: number) =>
  <const>{
    payload: {
      count,
    },
    type: ActionKeys.ORDERS_COUNT_SUCCESS,
  };

export const countOrderError = () =>
  <const>{
    type: ActionKeys.ORDERS_COUNT_ERROR,
  };

// trackEvent is true if the caller that dispatched this action is NOT calling tracking
// events, which means the user-tracking saga WILL call the tracking events instead
export const selectOrder = (payload: OrderSelection, trackEvent: boolean) =>
  <const>{
    payload,
    trackEvent,
    type: ActionKeys.ORDERS_SELECT_ORDER,
  };

export const clearSelectedOrders = (trackEvent: boolean) =>
  <const>{
    trackEvent,
    type: ActionKeys.ORDERS_CLEAR_SELECTED,
  };

export const selectCheckedOrder = (payload: OrderSelection) =>
  <const>{
    payload,
    type: ActionKeys.ORDERS_SELECT_CHECKED_ORDER,
  };

export const clearCheckedOrder = () =>
  <const>{
    type: ActionKeys.ORDERS_CLEAR_CHECKED_ORDERS,
  };

export const initiateBulkEdit = (payload: OrderSelection) =>
  <const>{
    payload,
    type: ActionKeys.ORDERS_INITIATE_BULK_EDIT,
  };

export const syncSelectedOrders = (payload: OrderSelection) =>
  <const>{
    payload,
    type: ActionKeys.ORDERS_SYNC_SELECTED_ORDERS,
  };

export const updateRowModelBeingEdited = (payload: GridSelectionModel) =>
  <const>{
    payload,
    type: ActionKeys.ORDERS_UPDATE_ROW_MODEL_BEING_EDITED,
  };

export const clearRowModelBeingEdited = () =>
  <const>{
    type: ActionKeys.ORDERS_CLEAR_ROW_MODEL_BEING_EDITED,
  };

// OrdersV2 drawer (in its current iteration) needs to fetch OrderV1 and BatchEntry
export const loadAndSelectOrders = (
  orderSelection: OrderSelection,
  rowModelBeingEdited: GridSelectionModel,
) =>
  <const>{
    payload: {
      orderSelection,
      rowModelBeingEdited,
    },
    type: ActionKeys.ORDERS_LOAD_AND_SELECT,
  };

/**
 * Trigger this action to store the orders and batchenries
 * remaining for label download due to any transaction errors
 * on other orders.
 * If the payload is sent empty then we clear this storage
 * @param payload OrderSelection optional
 * @returns
 */
export const storeOrdersRemainingLabelDownload = (payload?: OrderSelection) =>
  <const>{
    payload,
    type: ActionKeys.ORDERS_REMAINING_LABEL_DOWNLOAD,
  };

export const fetchOrderTags = () =>
  <const>{
    type: ActionKeys.ORDERS_FETCH_TAGS,
  };

export const updateOrderTags = (orderIds: string[]) =>
  <const>{
    payload: { orderIds },
    type: ActionKeys.ORDERS_UPDATE_TAGS,
  };

export const updateOrderTagsSuccess = (orderTags: OrderTagsMapping) =>
  <const>{
    payload: {
      orderTags,
    },
    type: ActionKeys.ORDERS_UPDATE_TAGS_SUCCESS,
  };

export const updateOrderTagsError = () =>
  <const>{
    type: ActionKeys.ORDERS_UPDATE_TAGS_ERROR,
  };

export const updateOrderTagsClear = () =>
  <const>{
    type: ActionKeys.ORDERS_UPDATE_TAGS_CLEAR,
  };

export const fetchOrderTagsSuccess = () =>
  <const>{
    type: ActionKeys.ORDERS_FETCH_TAGS_SUCCESS,
  };

export const fetchOrderTagsError = () =>
  <const>{
    type: ActionKeys.ORDERS_FETCH_TAGS_ERROR,
  };

export const startCheckout = (
  orders: OrderSelection,
  isSourceSidePanel?: boolean,
  preserveRateAfterPoll = false,
) =>
  <const>{
    payload: {
      isSourceSidePanel: !!isSourceSidePanel,
      orders,
      preserveRateAfterPoll,
    },
    type: ActionKeys.ORDERS_START_CHECKOUT,
  };

export const reStartCheckout = () =>
  <const>{
    type: ActionKeys.ORDERS_RESTART_CHECKOUT,
  };

export const retryCheckout = (orderSelection: OrderSelection) =>
  <const>{
    payload: {
      orderSelection,
    },
    type: ActionKeys.ORDERS_RETRY_CHECKOUT,
  };

export const toggleCheckoutModal = (orders: OrderSelection, preCheckoutQueue: PreCheckoutSteps[]) =>
  <const>{
    payload: {
      orders,
      preCheckoutQueue,
    },
    type: ActionKeys.ORDERS_CHECKOUT_TOGGLE,
  };

export const confirmCheckout = (
  hasAutomatedEmailNotifications: boolean,
  hasMergedDocuments: boolean,
) =>
  <const>{
    payload: {
      hasAutomatedEmailNotifications,
      hasMergedDocuments,
    },
    type: ActionKeys.ORDERS_CHECKOUT_CONFIRM,
  };

export const confirmCheckoutWithErrors = (
  hasAutomatedEmailNotifications?: boolean,
  hasMergedDocuments?: boolean,
) =>
  <const>{
    payload: {
      hasAutomatedEmailNotifications,
      hasMergedDocuments,
    },
    type: ActionKeys.ORDERS_CHECKOUT_CONFIRM_WITH_ERROR,
  };

export const setCheckoutRateAfterPoll = (
  carrierAccount: string,
  serviceLevelToken: string,
  prevBatchEntry: BatchEntryExtended,
  autoStartCheckout = true,
) =>
  <const>{
    payload: {
      autoStartCheckout,
      carrierAccount,
      prevBatchEntry,
      serviceLevelToken,
    },
    type: ActionKeys.ORDERS_SET_CHECKOUT_RATE_AFTER_POLL,
  };

export const removeCheckoutRateAfterPollEntry = (key: string) =>
  <const>{
    payload: { key },
    type: ActionKeys.ORDERS_REMOVE_CHECKOUT_RATE_AFTER_POLL_ENTRY,
  };

export const checkoutSendEmailNotifications = (
  orderObjectIds: OrderObjectId[],
  batchEntryIds: BatchEntryId[],
) =>
  <const>{
    payload: {
      batchEntryIds,
      orderObjectIds,
    },
    type: ActionKeys.ORDERS_CHECKOUT_SEND_EMAIL_NOTIFICATIONS,
  };

export const checkoutSendEmailNotificationsSuccess = () =>
  <const>{ type: ActionKeys.ORDERS_CHECKOUT_SEND_EMAIL_NOTIFICATIONS_SUCCESS };

export const checkoutSendEmailNotificationsError = (error: string) =>
  <const>{
    payload: {
      error,
    },
    type: ActionKeys.ORDERS_CHECKOUT_SEND_EMAIL_NOTIFICATIONS_ERROR,
  };

export const fetchOrderBatchEntry = (orderObjectId: string) =>
  <const>{
    payload: {
      orderObjectId,
    },
    type: ActionKeys.ORDER_BATCH_ENTRY_FETCH,
  };

export const fetchOrderBatchEntryError = (orderObjectId: string) =>
  <const>{
    payload: {
      orderObjectId,
    },
    type: ActionKeys.ORDER_BATCH_ENTRY_FETCH_ERROR,
  };

export const startBatchEntriesPolling = (orderObjectIds: string[]) =>
  <const>{
    payload: {
      orderObjectIds,
    },
    type: ActionKeys.ORDERS_BATCH_ENTRIES_POLL_START,
  };

export const stopBatchEntriesPolling = () =>
  <const>{
    type: ActionKeys.ORDERS_BATCH_ENTRIES_POLL_END,
  };

export const createAnotherLabel = (orderSelection: OrderSelection) =>
  <const>{
    payload: {
      orderSelection,
    },
    type: ActionKeys.ORDERS_CREATE_ANOTHER_LABEL,
  };

export const retryShipment = (orderSelection: OrderSelection) =>
  <const>{
    payload: {
      orderSelection,
    },
    type: ActionKeys.ORDERS_BATCH_ENTRY_RETRY_SHIPMENT,
  };

export const updateBatchEntriesShipmentDate = (
  orderSelection: OrderSelection,
  shipmentDate: moment.Moment | number,
) =>
  <const>{
    payload: {
      orderSelection,
      shipmentDate,
    },
    type: ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_SHIPMENT_DATE,
  };

export const updateBatchEntriesShipmentDateInitiated = (
  batchEntryIds: BatchEntryId[],
  shipmentDate: moment.Moment | number,
) =>
  <const>{
    payload: {
      batchEntryIds,
      shipmentDate,
    },
    type: ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_SHIPMENT_DATE_INITIATED,
  };

export const updateBatchEntriesShipmentDateSuccess = () =>
  <const>{
    type: ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_SHIPMENT_DATE_SUCCESS,
  };

export const updateBatchEntriesShipmentDateError = () =>
  <const>{
    type: ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_SHIPMENT_DATE_ERROR,
  };

export const openOrderSplitModal = (orderObjectId: string) =>
  <const>{
    payload: {
      orderObjectId,
    },
    type: ActionKeys.ORDER_SPLIT_MODAL_OPEN,
  };

export const orderScreenOpen = () =>
  <const>{
    type: ActionKeys.ORDER_SCREEN_OPEN,
  };

export const orderScreenClose = () =>
  <const>{
    type: ActionKeys.ORDER_SCREEN_CLOSE,
  };

export const fetchOrderBatchEntries = (orderObjectIds: string[]) =>
  <const>{
    payload: {
      orderObjectIds,
    },
    type: ActionKeys.ORDER_BATCH_ENTRIES_FETCH,
  };

export const fetchOrderBatchEntriesSuccess = (batchEntries: BatchEntryExtended[]) =>
  <const>{
    payload: {
      batchEntries,
    },
    type: ActionKeys.ORDER_BATCH_ENTRIES_FETCH_SUCCESS,
  };

export const closeOrderSplitModal = () =>
  <const>{
    type: ActionKeys.ORDER_SPLIT_MODAL_CLOSE,
  };

export const fetchOrderBatchEntriesError = (orderObjectIds: string[]) =>
  <const>{
    payload: {
      orderObjectIds,
    },
    type: ActionKeys.ORDER_BATCH_ENTRIES_FETCH_ERROR,
  };

export const fetchBatchEntryRules = (batchEntryId: number) =>
  <const>{
    payload: {
      batchEntryId,
    },
    type: ActionKeys.BATCH_ENTRY_RULES_FETCH,
  };

export const fetchBatchEntryRulesSuccess = (batchEntryId: number, rules: BusinessRule[]) =>
  <const>{
    payload: {
      batchEntryId,
      rules,
    },
    type: ActionKeys.BATCH_ENTRY_RULES_FETCH_SUCCESS,
  };

export const fetchBatchEntryRulesError = (error: string) =>
  <const>{
    payload: {
      error,
    },
    type: ActionKeys.BATCH_ENTRY_RULES_FETCH_ERROR,
  };

export const clearBatchEntryRules = (batchEntryId: number) =>
  <const>{
    payload: {
      batchEntryId,
    },
    type: ActionKeys.BATCH_ENTRY_RULES_CLEAR,
  };

export const undoSplitOrder = ({ orderObjectId }: { orderObjectId: string }) =>
  <const>{
    payload: {
      orderObjectId,
    },
    type: ActionKeys.ORDER_UNDO_SPLIT,
  };

export const createPartialFulfillmentRequests = ({
  orderObjectId,
  partialFulfillments,
}: {
  orderObjectId: string;
  partialFulfillments: PartialFulfillmentContents[];
}) =>
  <const>{
    payload: {
      orderObjectId,
      partialFulfillments,
    },
    type: ActionKeys.ORDER_CREATE_PARTIAL_FULFILLMENT_REQUESTS,
  };

export const createPartialFulfillmentRequestsError = ({
  error,
  orderObjectId,
}: {
  error: string;
  orderObjectId: OrderObjectId;
}) =>
  <const>{
    payload: {
      error,
      orderObjectId,
    },
    type: ActionKeys.ORDER_CREATE_PARTIAL_FULFILLMENT_REQUESTS_ERROR,
  };

export const createPartialFulfillmentRequestsSuccess = ({
  isReturn = false,
  isUndo = false,
  order,
}: {
  isReturn?: boolean;
  isUndo?: boolean;
  order: Order;
}) =>
  <const>{
    payload: {
      isReturn,
      isUndo,
      order,
    },
    type: ActionKeys.ORDER_CREATE_PARTIAL_FULFILLMENT_REQUESTS_SUCCESS,
  };

export const clearBatchEntries = (
  initialBatchEntries: BatchEntryExtended[],
  resetBatchEntries: BatchEntryExtended[],
) =>
  <const>{
    payload: {
      initialBatchEntries,
      resetBatchEntries,
    },
    type: ActionKeys.ORDERS_CLEAR_BATCH_ENTRIES,
  };

export const clearAutoSaveErrors = () =>
  <const>{
    type: ActionKeys.ORDERS_CLEAR_AUTO_SAVE_ERRORS,
  };

export const createOrderReturnBatchEntry = () =>
  <const>{
    type: ActionKeys.ORDER_CREATE_RETURN_BATCH_ENTRY,
  };

export const createOrderReturnBatchEntrySuccess = () =>
  <const>{
    type: ActionKeys.ORDER_CREATE_RETURN_BATCH_ENTRY_SUCCESS,
  };

export const createOrderReturnLabel = (
  orderObjectId: string,
  shipment: ReturnLabelBatchEntry,
  returnOfID?: number,
) =>
  <const>{
    payload: {
      orderObjectId,
      returnOfID,
      shipment,
    },
    type: ActionKeys.ORDER_CREATE_RETURN_LABEL,
  };

export const createOrderReturnLabelSuccess = (orderToBatchEntry: OrderToBatchEntry) =>
  <const>{
    payload: {
      orderToBatchEntry,
    },
    type: ActionKeys.ORDER_CREATE_RETURN_LABEL_SUCCESS,
  };

export const createOrderReturnLabelError = () =>
  <const>{
    type: ActionKeys.ORDER_CREATE_RETURN_LABEL_ERROR,
  };

export type OrdersActions =
  | ReturnType<typeof checkoutSendEmailNotifications>
  | ReturnType<typeof checkoutSendEmailNotificationsError>
  | ReturnType<typeof checkoutSendEmailNotificationsSuccess>
  | ReturnType<typeof clearAutoSaveErrors>
  | ReturnType<typeof clearBatchEntries>
  | ReturnType<typeof clearBatchEntryRules>
  | ReturnType<typeof clearCheckedOrder>
  | ReturnType<typeof clearRowModelBeingEdited>
  | ReturnType<typeof clearSelectedOrders>
  | ReturnType<typeof closeOrderSplitModal>
  | ReturnType<typeof confirmCheckout>
  | ReturnType<typeof confirmCheckoutWithErrors>
  | ReturnType<typeof countOrderError>
  | ReturnType<typeof countOrderSuccess>
  | ReturnType<typeof countOrdersFetch>
  | ReturnType<typeof createAnotherLabel>
  | ReturnType<typeof createBatchError>
  | ReturnType<typeof createBatchSuccess>
  | ReturnType<typeof createOrderReturnBatchEntry>
  | ReturnType<typeof createOrderReturnBatchEntrySuccess>
  | ReturnType<typeof createOrderReturnLabel>
  | ReturnType<typeof createOrderReturnLabelError>
  | ReturnType<typeof createOrderReturnLabelSuccess>
  | ReturnType<typeof createPartialFulfillmentRequests>
  | ReturnType<typeof createPartialFulfillmentRequestsError>
  | ReturnType<typeof createPartialFulfillmentRequestsSuccess>
  | ReturnType<typeof fetchBatchEntryRules>
  | ReturnType<typeof fetchBatchEntryRulesError>
  | ReturnType<typeof fetchBatchEntryRulesSuccess>
  | ReturnType<typeof fetchOrder>
  | ReturnType<typeof fetchOrderBatchEntries>
  | ReturnType<typeof fetchOrderBatchEntriesError>
  | ReturnType<typeof fetchOrderBatchEntriesSuccess>
  | ReturnType<typeof fetchOrderBatchEntry>
  | ReturnType<typeof fetchOrderBatchEntryError>
  | ReturnType<typeof fetchOrderError>
  | ReturnType<typeof fetchOrderSuccess>
  | ReturnType<typeof fetchOrderTags>
  | ReturnType<typeof fetchOrderTagsError>
  | ReturnType<typeof fetchOrderTagsSuccess>
  | ReturnType<typeof fetchOrders>
  | ReturnType<typeof fetchOrdersError>
  | ReturnType<typeof fetchOrdersSuccess>
  | ReturnType<typeof getOrderPackingSlipError>
  | ReturnType<typeof getPackingSlips>
  | ReturnType<typeof hideOrderError>
  | ReturnType<typeof hideOrderSuccess>
  | ReturnType<typeof hideOrders>
  | ReturnType<typeof initiateBulkEdit>
  | ReturnType<typeof markBatchEntriesProcessingShipment>
  | ReturnType<typeof newOrdersFound>
  | ReturnType<typeof openOrderSplitModal>
  | ReturnType<typeof orderClean>
  | ReturnType<typeof orderEditBegin>
  | ReturnType<typeof orderEditEnd>
  | ReturnType<typeof orderScreenClose>
  | ReturnType<typeof orderScreenOpen>
  | ReturnType<typeof patchBatchEntries>
  | ReturnType<typeof patchBatchEntriesError>
  | ReturnType<typeof patchBatchEntriesSuccess>
  | ReturnType<typeof patchUpdateBatchEntriesExtras>
  | ReturnType<typeof patchUpdateBatchEntriesExtrasError>
  | ReturnType<typeof patchUpdateBatchEntriesExtrasPackingSlipNote>
  | ReturnType<typeof patchUpdateBatchEntriesExtrasReset>
  | ReturnType<typeof patchUpdateBatchEntriesExtrasSuccess>
  | ReturnType<typeof pollOrdersStart>
  | ReturnType<typeof removeCheckoutRateAfterPollEntry>
  | ReturnType<typeof retryCheckout>
  | ReturnType<typeof retryShipment>
  | ReturnType<typeof saveLastOrderId>
  | ReturnType<typeof selectCheckedOrder>
  | ReturnType<typeof selectOrder>
  | ReturnType<typeof setCheckoutRateAfterPoll>
  | ReturnType<typeof startBatchEntriesPolling>
  | ReturnType<typeof startCheckout>
  | ReturnType<typeof stopBatchEntriesPolling>
  | ReturnType<typeof storeOrdersRemainingLabelDownload>
  | ReturnType<typeof syncOrders>
  | ReturnType<typeof syncOrdersError>
  | ReturnType<typeof syncOrdersSuccess>
  | ReturnType<typeof syncSelectedOrders>
  | ReturnType<typeof toggleCheckoutModal>
  | ReturnType<typeof updateBatchEntriesAddress>
  | ReturnType<typeof updateBatchEntriesAddressError>
  | ReturnType<typeof updateBatchEntriesAddressSuccess>
  | ReturnType<typeof updateBatchEntriesExtras>
  | ReturnType<typeof updateBatchEntriesExtrasSuccess>
  | ReturnType<typeof updateBatchEntriesServiceLevelToken>
  | ReturnType<typeof updateBatchEntriesServiceLevelTokenError>
  | ReturnType<typeof updateBatchEntriesServiceLevelTokenSuccess>
  | ReturnType<typeof updateBatchEntriesShipmentDate>
  | ReturnType<typeof updateBatchEntriesShipmentDateError>
  | ReturnType<typeof updateBatchEntriesShipmentDateInitiated>
  | ReturnType<typeof updateBatchEntriesShipmentDateSuccess>
  | ReturnType<typeof updateOrderParcel>
  | ReturnType<typeof updateOrderParcelError>
  | ReturnType<typeof updateOrderParcelSuccess>
  | ReturnType<typeof updateOrderTags>
  | ReturnType<typeof updateOrderTagsClear>
  | ReturnType<typeof updateOrderTagsError>
  | ReturnType<typeof updateOrderTagsSuccess>
  | ReturnType<typeof updateRowModelBeingEdited>;
